import { useOnValueChange } from '@calendly/hooks';
import { BareButton } from '@calendly/ui/components/bare-button';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon, CannotEditIcon } from '@calendly/ui/components/icons';
import { TitleText } from '@calendly/ui/components/typography';
import { colorIconStandard } from '@calendly/ui/theme';
import classNames from 'classnames';
import { createContext, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FullStoryMask } from '../../../../../shared/fullstory_privacy';
import { LockingRuleSwitch } from '../../sections/components';
import { EditingRestricted } from '../../sections/components/editing_restricted';
export const PanelContext = createContext({});
const buttonGap = 4;
const dynamicMaxHeightShift = buttonGap;
const buttonUseAlternateStyleCss = "b1vwnpi2";
const buttonIsExpandedCss = "bxltnx4";
const buttonCss = "b124b57k";
const Button = forwardRef((props, ref) => {
  const {
    useAlternateStyle,
    isExpanded,
    ...restProps
  } = props;
  return <BareButton ref={ref} {...restProps} className={classNames(buttonCss, {
    [buttonUseAlternateStyleCss]: useAlternateStyle,
    [buttonIsExpandedCss]: isExpanded
  }, props.className)} />;
});
const containerUseAlternateStyleCss = "c1ic9kk";
const containerIsExpandedCss = "c19ov4gf";
const containerRoundedCss = "cu27ey";
const containerNoBackgroundCss = "c9iho7x";
const containerCss = "cbqbsyq";
const Container = forwardRef((props, ref) => {
  const {
    useAlternateStyle,
    isExpanded,
    rounded,
    noBackground,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, {
    [containerUseAlternateStyleCss]: useAlternateStyle,
    [containerIsExpandedCss]: isExpanded,
    [containerRoundedCss]: rounded,
    [containerNoBackgroundCss]: noBackground
  }, props.className)} />;
});
const panelContainerSkipAnimationCss = "p18aewkx";
const panelContainerIsSlidingInCss = "p14elqqb";
const panelContainerIsSlidingOutCss = "pwn5b6a";
const panelContainerCss = "pnxckqp";
const PanelContainer = forwardRef((props, ref) => {
  const {
    skipAnimation,
    isSlidingIn,
    isSlidingOut,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(panelContainerCss, {
    [panelContainerSkipAnimationCss]: skipAnimation,
    [panelContainerIsSlidingInCss]: isSlidingIn,
    [panelContainerIsSlidingOutCss]: isSlidingOut
  }, props.className)} />;
});
const panelBodyUseAlternateStyleCss = "p1bklhfu";
const panelBodySkipAnimationCss = "p185ts4f";
const panelBodyIsFadingInCss = "p8ez0a4";
const panelBodyIsFadingOutCss = "pxaeo26";
const panelBodyCss = "p1swtzxq";
const PanelBody = forwardRef((props, ref) => {
  const {
    useAlternateStyle,
    skipAnimation,
    isFadingIn,
    isFadingOut,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(panelBodyCss, {
    [panelBodyUseAlternateStyleCss]: useAlternateStyle,
    [panelBodySkipAnimationCss]: skipAnimation,
    [panelBodyIsFadingInCss]: isFadingIn,
    [panelBodyIsFadingOutCss]: isFadingOut
  }, props.className)} />;
});
const columnContainerCss = "cuczl6s";
const ColumnContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(columnContainerCss, props.className)} />;
});
const headerCss = "h34nho8";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const titleContainerCss = "t1b7e3me";
const TitleContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(titleContainerCss, props.className)} />;
});
const subheaderCss = "sebm6j7";
const Subheader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(subheaderCss, props.className)} />;
});
export const AccordionPanel = ({
  children,
  panelKey,
  expand,
  onSelected,
  disabled = false,
  skipAnimation = false,
  lockingDisables = true,
  showCannotEditIcon = false,
  showEditingRestricted = false,
  showLockingRuleSwitch = false,
  lockingRuleName = null,
  title,
  titleId,
  titleBadge = null,
  sidebar = null,
  subheader = null,
  noBackground = false,
  rounded = false,
  useAlternateStyle = false
}) => {
  const subheaderRef = useRef(null);
  const firstTimeOpenedRef = useRef(null);
  const panelContainerRef = useRef(null);
  const [isExpanded, setExpanded] = useState(expand);
  const buttonId = `${panelKey}-button`;
  const panelId = `${panelKey}-panel`;
  const containerId = `${panelKey}-panel-container`;
  const contentId = `${panelKey}-panel-content-container`;
  const contextValue = useMemo(() => ({
    firstTimeExpanded: isExpanded && firstTimeOpenedRef.current
  }), [isExpanded]);
  const onAnimationEnd = useCallback(() => {
    if (!expand) {
      setExpanded(false);
      if (firstTimeOpenedRef.current) {
        firstTimeOpenedRef.current = false;
      }
    } else if (panelContainerRef.current) {
      panelContainerRef.current.style.overflow = 'visible';
    }
  }, [expand]);
  useOnValueChange(expand, () => {
    if (skipAnimation) setExpanded(expand);
    if (expand) {
      setExpanded(true);
      if (firstTimeOpenedRef.current === null) {
        firstTimeOpenedRef.current = true;
      }
    } else if (panelContainerRef.current) {
      panelContainerRef.current.style.overflow = 'hidden';
    }
  });
  useEffect(() => {
    if (panelContainerRef.current && subheaderRef.current?.clientHeight) {
      const dynamicMaxHeight = `${subheaderRef.current.clientHeight + dynamicMaxHeightShift}px`;
      panelContainerRef.current.style.setProperty('--dynamic-max-height', dynamicMaxHeight);
    }
  }, []);
  const canShowSubheader = !isExpanded && subheader;
  return <PanelContext.Provider value={contextValue}>
      <Container data-component={containerId} data-testid={containerId} isExpanded={isExpanded} noBackground={noBackground} useAlternateStyle={useAlternateStyle} rounded={rounded}>
        <Button aria-controls={panelId} aria-expanded={isExpanded ? 'true' : 'false'} id={buttonId} isExpanded={isExpanded} onClick={() => disabled ? null : onSelected(panelKey, !isExpanded)} useAlternateStyle={useAlternateStyle}>
          {sidebar}
          <ColumnContainer>
            <Header>
              <TitleContainer>
                <TitleText id={titleId} size="small">
                  <FullStoryMask>{title}</FullStoryMask>
                </TitleText>
                {showCannotEditIcon && <CannotEditIcon data-component="lock-icon" color={colorIconStandard} />}
              </TitleContainer>
              <TitleContainer>
                {titleBadge}
                {isExpanded ? <ArrowChevronUpMiniIcon /> : <ArrowChevronDownMiniIcon />}
              </TitleContainer>
            </Header>
            {canShowSubheader && <Subheader ref={subheaderRef}>{subheader}</Subheader>}
          </ColumnContainer>
        </Button>
        <PanelContainer data-testid={contentId} isSlidingIn={isExpanded} isSlidingOut={!expand} onAnimationEnd={skipAnimation ? null : onAnimationEnd} ref={panelContainerRef} skipAnimation={skipAnimation}>
          {isExpanded && <PanelBody aria-labelledby={buttonId} id={panelId} data-component={panelId} isFadingIn={expand} isFadingOut={!expand} role="region" skipAnimation={skipAnimation} useAlternateStyle={useAlternateStyle}>
              {showLockingRuleSwitch && <LockingRuleSwitch ruleName={lockingRuleName} />}
              {showEditingRestricted && <EditingRestricted subsection lockingDisables={lockingDisables} />}
              {children}
            </PanelBody>}
        </PanelContainer>
      </Container>
    </PanelContext.Provider>;
};

require("./panel.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./panel.js");